import React, { useEffect, useMemo, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import { Stack } from '@sureco/design-system'
import { getCensusFiles } from '../../api/census-enrollment-platform-api-client'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { SortDirection } from '../../../../types/sorted-request.dto'
import {
  CENSUS_REVIEW_PATH,
  DEFAULT_FILES_PAGINATION_LIMIT,
} from '../../constants'
import { CensusFileWithRowStatsDto } from '../../api/dto/census-file-with-row-stats.dto'
import { getCensusFilesColDefs } from '../../utils/get-census-files-col-defs'
import { CensusClientFilter } from '../../types/census-client-filter'
import { useCensusV2Clients } from '../../hooks/use-census-v2-clients'
import { filterClients } from '../utils/filter-clients'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'

type Props = {
  clientNameLookup: Record<string, string>
  filter: CensusClientFilter
}

const ActivityHistory = ({ clientNameLookup, filter }: Props) => {
  const [grid, setGrid] = useState<GridReadyEvent>()
  const { clients } = useCensusV2Clients()
  const role = useSelector(authSelectors.getRole) as string

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<CensusFileWithRowStatsDto>
      ) => {
        const limit = DEFAULT_FILES_PAGINATION_LIMIT
        let sort: string | undefined
        let order: SortDirection | undefined
        try {
          const { startRow, sortModel } = params.request
          if (sortModel.length > 0) {
            const { colId, sort: direction } = sortModel[0] // Take the first sort entry
            sort = colId
            order =
              direction === 'desc' ? SortDirection.desc : SortDirection.asc
          }
          const status = [
            CensusFileRowStatusEnum.WITHDRAWN,
            CensusFileRowStatusEnum.REJECTED,
            CensusFileRowStatusEnum.COMPLETE,
            CensusFileRowStatusEnum.DELETED,
          ]

          const page = Math.floor((startRow ?? 1) / limit) + 1

          const clientIds = filterClients(clients, filter, role).map(
            (client) => client.id
          )

          const { meta: pagination, data: files } = await getCensusFiles({
            status,
            page,
            limit,
            sort,
            order,
            clientPublicKey: clientIds,
            includeTestClients: filter?.includeTestCompanies,
            dateFrom: filter?.dateFrom,
            dateTo: filter?.dateTo,
          })

          params.success({ rowData: files, rowCount: pagination.total })
        } catch (error) {
          console.error(error)
          params.fail()
        }
      },
    }),
    [filter]
  )

  useEffect(() => {
    if (grid) {
      grid.api.setGridOption('serverSideDatasource', datasource)
      grid.api.purgeInfiniteCache()
      grid.api.refreshServerSide({ purge: true })
    }
  }, [grid, datasource])

  const clickView = (publicKey: string) => {
    window.open(`${CENSUS_REVIEW_PATH}/${publicKey}/activity-history`, '_blank')
  }
  const columnDefs = getCensusFilesColDefs({
    hasSubmittedColumn: true,
    hasZendeskStatusColumn: true,
    clientNameLookup,
    clickView,
  })

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        Activity History
      </Stack>
      <AgGridReact
        rowStyle={{
          backgroundColor: 'white',
          marginBottom: '20px',
        }}
        className="censusReviewGrid ag-theme-alpine"
        getRowId={({ data }) => data.id}
        pagination
        paginationPageSize={10}
        cacheBlockSize={10}
        key={filter.selectedClient?.id ?? 'no-client'}
        columnDefs={columnDefs}
        rowModelType="serverSide"
        onGridReady={(params) => setGrid(params)}
        domLayout="autoHeight"
        suppressHorizontalScroll
        paginationPageSizeSelector={[10, 20, 50, 100]}
      />
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default ActivityHistory
